import type { PrivacyStrings } from "../../types";

export const privacy: PrivacyStrings = {
  "title": "Privacy Policy",
  "lastUpdated": "Last updated: 3 November 2026",
  "sections": [
    {
      "title": "Overview",
      "paragraphs": [
        {
          "nodes": [
            "This policy covers the Cate desktop app, Cate Connect and this website. Cate is a local-first workspace: files and sessions are stored on the computer running the workspace runtime. You can access them from paired devices. The linked mobile supplement explains the additional processing specific to Cate Mobile for iPhone. Both notices share the controller and rights described here."
          ]
        },
        {
          "nodes": [
            {
              "link": "Cate Mobile privacy supplement",
              "href": "/mobile/privacy"
            }
          ]
        }
      ]
    },
    {
      "title": "Controller",
      "paragraphs": [
        {
          "pre": true,
          "nodes": [
            "0-AI UG (haftungsbeschränkt)\nNeckartalstraße 127\n70376 Stuttgart\nGermany"
          ]
        },
        {
          "nodes": [
            "Email: ",
            {
              "mail": "contact@cero-ai.com"
            }
          ]
        }
      ]
    },
    {
      "title": "Desktop crash reporting and usage analytics",
      "paragraphs": [
        {
          "nodes": [
            "Packaged desktop builds include crash reporting through Sentry and usage analytics through our self-hosted analytics service. From desktop version 1.3.1 onward these are always on and cannot be disabled. Usage events include app launches, installs, updates and feature use, with technical context such as app version, platform and locale. A random installation identifier links events from the same installation; it is not your name or email address."
          ]
        },
        {
          "nodes": [
            "Usage events exclude workspace contents, file paths and AI messages. Crash reports contain errors, stack traces and technical context; home-directory paths are masked and navigation URLs are reduced to their origin. We process diagnostics on the basis of our legitimate interest in stability and product improvement (Art. 6(1)(f) GDPR). This desktop reporting does not describe the iOS app; see the mobile supplement."
          ]
        }
      ]
    },
    {
      "title": "AI providers and websites you open",
      "paragraphs": [
        {
          "nodes": [
            "Agent requests go from the workspace runtime to the provider you configure, using your account or API key and subject to that provider’s terms. Prompts can include code, file excerpts, messages, attachments and tool output selected by the agent. Browser panels contact the websites you open. Those services apply their own privacy policies and may process data outside the EEA."
          ]
        }
      ]
    },
    {
      "title": "Cate Connect",
      "paragraphs": [
        {
          "nodes": [
            "Cate Connect helps paired devices establish connections and relays traffic when needed. Workspace traffic is encrypted between the paired devices; the service cannot read its plaintext. Connection setup processes runtime identifiers, public keys, connection signaling and network metadata such as IP addresses. Our hosting and network providers process the traffic needed to operate the service."
          ]
        },
        {
          "nodes": [
            "Active connection registrations are held in memory and removed when the runtime disconnects. Operational logs are used as needed to diagnose errors and protect the service. We process connection data to provide the requested service (Art. 6(1)(b) GDPR) and necessary operational data for security and reliability (Art. 6(1)(f)). To revoke a paired device’s access, remove it in Cate on the runtime computer. The mobile supplement describes iOS push delivery and device storage."
          ]
        }
      ]
    },
    {
      "title": "Automatic updates",
      "paragraphs": [
        {
          "nodes": [
            "Packaged desktop builds check GitHub for newer versions. GitHub receives the network information needed to deliver the response, including your IP address, under its own privacy statement. Apple distributes and updates the iOS app through the App Store."
          ]
        }
      ]
    },
    {
      "title": "Data we process on the website",
      "paragraphs": [
        {
          "nodes": [
            "This website uses our self-hosted analytics at analytics.cero-ai.com to record page views and referrers. The tracker does not set cookies or create a persistent visitor identifier. Network services receive your IP address to deliver and protect the site. We process this information for our legitimate interests in operating and understanding the website (Art. 6(1)(f) GDPR). Your chosen language and theme are stored locally in your browser."
          ]
        },
        {
          "nodes": [
            "If you subscribe to the newsletter, we use your email address to send product updates with your consent (Art. 6(1)(a) GDPR), using Resend as our email provider. You can unsubscribe at any time, which removes your address from the mailing list. The site also loads release information from GitHub, a Product Hunt badge and fonts from Google; those services receive network information when their content loads."
          ]
        }
      ]
    },
    {
      "title": "Your rights",
      "paragraphs": [
        {
          "nodes": [
            "Under the GDPR you have the right to access your personal data, to have it corrected or erased, to restrict or object to its processing, and to data portability. Where processing is based on consent, you may withdraw that consent at any time. To exercise any of these rights, contact us at ",
            {
              "mail": "contact@cero-ai.com"
            },
            "."
          ]
        },
        {
          "nodes": [
            "You also have the right to lodge a complaint with a supervisory authority, in our case the State Commissioner for Data Protection and Freedom of Information of Baden-Württemberg."
          ]
        }
      ]
    },
    {
      "title": "Changes to this policy",
      "paragraphs": [
        {
          "nodes": [
            "We may update this policy as the app and website evolve. Material changes will be reflected by the \"last updated\" date above. If you have any questions, reach us at ",
            {
              "mail": "contact@cero-ai.com"
            },
            "."
          ]
        }
      ]
    }
  ]
};
